/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps } from 'preact';
import { useReducer, useRef } from 'preact/hooks';
import { BCP47Locale } from '#UNSAFE_IntlDateTime';
import { DateField } from './DateField';
import { CalendarDate, DateGranularity, DatePlaceholders, EditableDateSegment } from './types';
import { DateSegment } from './DateSegment';
import { dateReducer, getMonthAria, numberToString } from './dateReducer';
import { calendarDatesAreDifferent } from '../utils/PRIVATE_dayUtils';
import {
  getDaysInMonth,
  getMonthsInYear,
  MIN_ISO_YEAR,
  MAX_ISO_YEAR,
  MIN_ISO_DAY,
  MIN_ISO_MONTH
} from '../utils/UNSAFE_calendarDateUtils';

type DateFieldType = ComponentProps<typeof DateField>;

type useDateFieldProps = {
  ariaLabels: Record<EditableDateSegment, string>;
  dateMasks: DatePlaceholders;
  granularity: DateGranularity;
  locale: BCP47Locale;
  value: DateFieldType['value'];
};

type DateSegmentState = Readonly<ComponentProps<typeof DateSegment>>;

type DateFieldState = {
  readonly currentDate: CalendarDate | undefined;
  readonly isCompleteDate: boolean;
  readonly isDateSelected: boolean;
  readonly isPartialDate: boolean;
  readonly orderedSegments: EditableDateSegment[];
  readonly segments: DateSegmentState[];
};

/**
 * Hook that returns a DateFieldState and a dispatch method to request changes to that state.
 */
const useDateField = ({
  ariaLabels,
  dateMasks,
  granularity,
  locale,
  value: currentValue
}: useDateFieldProps) => {
  // Construct an ordered list of the editable segments, i.e. ['month', 'day', 'year'].
  const fdm = dateMasks.filter(({ type }) => type !== 'literal');
  const orderedSegments = fdm.map(({ type }) => type) as EditableDateSegment[];

  // Derive some data from currentValue that will become part of our initial state.
  const yearValue = currentValue?.year;
  const monthValue = currentValue?.month;
  const dayValue = currentValue?.day;
  const monthMax = getMonthsInYear(yearValue ?? '*');
  const dayMax = getDaysInMonth(monthValue ?? '*', yearValue ?? '*');
  const isCompleteDate = !!yearValue && !!monthValue && (granularity === 'month' || !!dayValue);
  const isPartialDate = !!yearValue || !!monthValue || !!dayValue;
  const yearValueStr = yearValue ? numberToString(yearValue) : undefined;
  const monthValueStr = monthValue ? numberToString(monthValue) : undefined;
  const dayValueStr = dayValue ? numberToString(dayValue) : undefined;
  const monthAria = monthValue ? getMonthAria(locale, monthValue, yearValue) : undefined;

  const daySeg = [
    {
      type: 'day' as EditableDateSegment,
      'aria-label': ariaLabels.day,
      'aria-valuemin': MIN_ISO_DAY,
      'aria-valuemax': dayMax,
      'aria-valuenow': dayValue,
      'aria-valuetext': dayValueStr,
      isComplete: !!dayValue,
      isSelected: false,
      text: dayValueStr
    }
  ];

  // Create state for each editable segment of a date, i.e. year, month, or day.
  const editableSegments = [
    {
      type: 'year' as EditableDateSegment,
      'aria-label': ariaLabels.year,
      'aria-valuemin': MIN_ISO_YEAR,
      'aria-valuemax': MAX_ISO_YEAR,
      'aria-valuenow': yearValue,
      'aria-valuetext': yearValueStr,
      isComplete: !!yearValue,
      isSelected: false,
      text: yearValueStr
    },
    {
      type: 'month' as EditableDateSegment,
      'aria-label': ariaLabels.month,
      'aria-valuemin': MIN_ISO_MONTH,
      'aria-valuemax': monthMax,
      'aria-valuenow': monthValue,
      'aria-valuetext': monthAria,
      isComplete: !!monthValue,
      isSelected: false,
      text: monthValueStr
    },
    ...(granularity === 'day' ? daySeg : [])
  ];

  // Create the initial state to pass to our reducer.
  const initialState: DateFieldState = {
    currentDate: currentValue,
    isCompleteDate,
    isDateSelected: false,
    isPartialDate,
    orderedSegments,
    segments: editableSegments
  };

  const [state, dispatch] = useReducer(dateReducer, initialState);

  // currentValue is the controlled value passed from the parent.
  const lastValueFromParentRef = useRef<CalendarDate | undefined>(currentValue);
  const dateResetRef = useRef<boolean>(false);

  // If a new value is being pushed from the parent, and that value differs from
  // the current value of the reducer, then this is a programmatic update
  // and we need to call 'reset' to update the reducer state. Otherwise, the only
  // time the reducer state is updated from the value is on mount.
  if (calendarDatesAreDifferent(lastValueFromParentRef.current, currentValue)) {
    lastValueFromParentRef.current = currentValue;
    if (calendarDatesAreDifferent(currentValue, state.currentDate)) {
      dispatch({ actionType: 'reset', data: initialState });
      dateResetRef.current = true;
    }
  }

  return { dateResetRef, dispatch, state };
};

export { useDateField };
export type { DateFieldState, DateSegmentState };
